import React, { useCallback, useReducer, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Select from 'antd/lib/select'
import getComponentKey from '@/utils/getComponentKey'
import { useLocalization } from '@/hooks/localization'
import { debounce } from 'lodash'
import { CancelToken } from '@/utils/fetch'
import ErrorBoundary from '@/components/ErrorBoundary'
import Icon from '@/components/Icon'
import { Link } from 'react-router-dom'
import pascalCase from '@/utils/pascalCase'
import keys from 'lodash/keys'
import { isDefinedAndNotEmpty } from '@/utils/isDefined'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'

const initialState = {
  loading: false,
  total: 0,
  dataOffset: 0,
  loadedOffset: 0,
  filter: null,
  data: [],
  requestStarted: false,
  emptyResult: false,
  errorOccurred: false
}

function reducer (state, { type, ...action }) {
  switch (type) {
    case 'reset': {
      return { ...initialState }
    }
    case 'search': {
      return { ...state, filter: action.value, emptyResult: false, data: [], dataOffset: 0, loadedOffset: 0 }
    }
    case 'set': {
      return { ...state, ...action }
    }
    case 'clear': {
      return { ...state, data: [], dataOffset: 0, loadedOffset: 0, filter: null, emptyResult: false }
    }
    // Appends new data to state and runs a logic on them
    case 'processData': {
      const { localize, clientSide, isCodeList, textField, value, originalValue, newData, newTotal } = action
      let mergedData

      // Does not process data if data array contains all available data
      if (state.data.length === newTotal) {
        return state
      }

      if (isCodeList) {
        const val = typeof value === 'object' && value ? value.id : value
        if (originalValue) {
          // original value is omitted for the first time and then added as first item
          mergedData = state.data.concat(
            newData.filter(x => x.isValid && ((val !== originalValue && x.id === val) || (x.id !== originalValue)))
          )
          const originalItem = newData.find(x => x.id === originalValue)
          if (originalItem) {
            mergedData.unshift(originalItem)
          }
        } else {
          mergedData = state.data.concat(newData.filter(x => x.isValid || x.id === val))
        }
      } else {
        // Filter out originalValue from newData if already in state.data
        if (state.data.find(x => x.id === value)) {
          mergedData = state.data.concat(newData.filter(x => x.id !== value))
        } else {
          mergedData = state.data.concat(newData)
        }
      }

      // Deals with empty items
      mergedData.forEach(item => {
        if (item[textField] === '') {
          if (item.id) {
            item[textField] = `Empty ${textField} - Id: ${item.id}`
          } else {
            item[textField] = '-'
          }
        }

        if (isCodeList && !item.isValid && !item[textField].endsWith(` (${localize('Common.ObsoleteItem')})`)) {
          item[textField] = item[textField] + ` (${localize('Common.ObsoleteItem')})`
        }
      })

      if (state.filter && clientSide) {
        const lowerFilter = state.filter.toLowerCase()
        mergedData = mergedData.filter(x => x[textField].toLowerCase().includes(lowerFilter))
      }

      return {
        ...state,
        data: mergedData,
        total: newTotal,
        loadedOffset: mergedData.length,
        emptyResult: newTotal === 0 || (clientSide && state.data.length === 0)
      }
    }
  }
}

export const DropDownList = React.memo(function DropDownList (props) {
  const {
    value,
    originalValue,
    refreshValue,
    clientSide,
    dataSource,
    isCodeList,
    onChange,
    itemsDetail,
    disabled,
    noSorting = false
  } = props
  // Property datasourceString is used as a changed property for useDebounceEffect/useEffect, inserting datasource (function)
  // directly causes unnecessary calling of those events (useDebounceEffect/useEffect). In this specific case it led to
  // calling dispatch reset and then another extra gathering data from server
  const datasourceString = (dataSource || '').toString()
  const { localizeProps, localize } = useLocalization()
  const [state, dispatch] = useReducer(reducer, { ...initialState })
  const { loading, total, dataOffset, loadedOffset, filter, data, emptyResult, requestStarted, errorOccurred } = state
  const cancellation = useRef(CancelToken.source())
  const pageSize = props.pageSize || 20
  const hasDataSource = typeof props.dataSource !== 'undefined'
  const textField = props.textField || localizeProps()

  const onSearch = useMemo(
    () => debounce((value) => dispatch({ type: 'search', value }), 750),
    [])

  // start loading indicator
  const startLoadingIndicator = () => dispatch({ type: 'set', loading: true, requestStarted: true })

  // Call reducer processData action
  const processData = (data, total) => {
    dispatch({
      type: 'processData',
      newData: data,
      newTotal: total,
      localize,
      clientSide,
      isCodeList,
      textField,
      value,
      originalValue
    })
  }

  // Stop loading
  function stopLoading (errorOccurred = false) {
    dispatch({ type: 'set', loading: false, requestStarted: errorOccurred, errorOccurred })
  }

  useDebouncedEffect(() => {
    dispatch({ type: 'reset' })
  }, [datasourceString, refreshValue])

  // Data loading side effect
  useDebouncedEffect(() => {
    if (hasDataSource) {
      if (requestStarted || // Prevents multiple simultaneous requests
        total < dataOffset || // All data loaded
        data.length > dataOffset || // Enough data loaded for now
        emptyResult || // No result
        disabled) {
        return
      }

      const request = {
        take: pageSize,
        skip: loadedOffset,
        filter: filter ? {
          filters: [{ field: textField, operator: 'contains', value: filter }],
          logic: 'and'
        } : null,
        sort: [!noSorting && { dir: 'asc', field: textField }].filter(Boolean)
      }

      if (clientSide) {
        if (typeof dataSource === 'function') {
          startLoadingIndicator()
          dataSource().then(data => {
            processData(data, data.length)
            stopLoading()
          }).catch(() => stopLoading(true))
        } else {
          processData(dataSource, dataSource.length)
        }
      } else {
        startLoadingIndicator()
        const future = dataSource.length > 2
          ? dataSource(request, value, { cancelToken: cancellation.current.token })
          : dataSource(request, { cancelToken: cancellation.current.token })
        future.then(response => {
          processData(
            response.data ? response.data : response,
            typeof response.total === 'number' ? response.total : response.length
          )
          stopLoading()
        }).catch(() => stopLoading(true))
      }
    }
    return () => {
      onSearch.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSide, filter, hasDataSource, isCodeList, originalValue, pageSize, dataOffset, loadedOffset,
    textField, noSorting, datasourceString])

  useDebouncedEffect(() => {
    return () => {
      onSearch.cancel()
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cancellation.current.cancel()
    }
  }, [onSearch])

  const onScroll = useCallback((e) => {
    e.persist()
    const target = e.target

    // Sets newOffsets to next logical offset or total size of data if not enough remain for next page
    const newOffset = Math.min(dataOffset + pageSize, total)

    if (Math.ceil(target.scrollTop) + target.offsetHeight !== target.scrollHeight || // Bottom of list not hit
      data.length === total || // All data loaded
      newOffset > loadedOffset + pageSize // Called too early (data didn't load in time)
    ) {
      return
    }

    dispatch({ type: 'set', dataOffset: newOffset })
  }, [dataOffset, pageSize, total, data.length, loadedOffset])

  const onDropDownChange = useCallback((e) => {
    const { value, item, props } = e
    if (!value && (dataOffset !== 0 || filter !== null)) {
      dispatch({ type: 'clear' })
    }

    onChange({ value: value || null, item: item || null, props }, props)
  }, [filter, dataOffset, onChange])

  if (hasDataSource) {
    const { valueField, disabled, value, transform, ...rest } = props
    return (
      <ErrorBoundary>
        <DropDownListSimple
          {...rest}
          allowClear={isDefinedAndNotEmpty(value)}
          data={typeof transform === 'function' ? transform(data) : data}
          valueField={valueField || 'id'}
          textField={textField}
          disabled={disabled}
          onSearch={onSearch}
          onChange={onDropDownChange}
          value={value}
          onPopupScroll={onScroll}
          loading={loading}
          filterOption={false}
          showSearch
          isCodeList
          hasOriginalValue={!!originalValue}
          itemsDetail={itemsDetail}
          errorOccurred={errorOccurred}
        />
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <DropDownListSimple allowClear={isDefinedAndNotEmpty(value)} {...props} />
    </ErrorBoundary>
  )
})

const DropDownListSimple = React.memo(function DropDownListSimple (props) {
  const { isCodeList, hasOriginalValue, loading, itemsDetail, errorOccurred, groupBy } = props
  const { localize } = useLocalization()
  const { OptGroup } = Select

  function renderOptions (item, baseKey, valueField, textField, detail) {
    const { Option } = Select
    if (typeof item === 'object') {
      return (<Option key={baseKey + '_' + item.id} value={item[valueField]} item={item}>
        <span className={'flex'}>
          <span>{item[textField]} {detail && detail.field && `(${pascalCase(detail.field)}: ${item[detail.field]})`}</span>
          {renderDetail(detail, item[valueField])}
        </span>
      </Option>)
    } else {
      return (<Option key={baseKey + '_' + item.id} value={item}><span>{item}</span> {renderDetail(detail)}</Option>)
    }
  }

  function onChange (value, option) {
    props.onChange({
      item: option && option.props && option.props.item,
      value,
      props
    })
  }

  const groupOptions = () => {
    const baseKey = getComponentKey().toString()
    const options = (data && data.map(x => renderOptions(x, baseKey, props.valueField, props.textField, itemsDetail))) || []
    if (groupBy && typeof groupBy === 'string') {
      const groupedData = {}
      data.forEach(x => {
        const groupName = x[groupBy]
        const group = groupedData[groupName] || []
        groupedData[groupName] = group
        group.push(x)
      })

      return (keys(groupedData).map(group =>
        <OptGroup
          key={group}
          label={group}>{groupedData[group].map(x => renderOptions(x, baseKey, props.valueField, props.textField, itemsDetail))}
        </OptGroup>))
    } else if (isCodeList && options.length > 1 && hasOriginalValue) {
      return ([
        <OptGroup key={baseKey + '_orig'} label={localize('Common.OriginalItem')}>
          {[options[0]]}
        </OptGroup>,
        <OptGroup key={baseKey + '_other'} label={localize('Common.OtherItems')}>
          {options.slice(1)}
        </OptGroup>
      ])
    }
    return options
  }

  function renderDetail (detailInfo, detailValue) {
    if (!detailInfo || !detailInfo.link) {
      return
    }

    const text = detailInfo.text ? detailInfo.text : localize('Common.Detail').toLowerCase()

    if (detailInfo.icon) {
      return <Link
        to={detailInfo.link.concat(detailValue)}
        onClick={e => e.stopPropagation()}
        target='_blank'><Icon
          name={detailInfo.icon} />{text}
      </Link>
    } else {
      return <Link
        to={detailInfo.link.concat(detailValue)}
        onClick={e => e.stopPropagation()}
        target='_blank'>{text}
      </Link>
    }
  }

  let value = props.value === null ? undefined : props.value
  let data = props.data

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if ((loading || props.disabled) && (!data || data.length === 0)) {
      data = [value]
    }

    value = value[props.valueField]
  }

  if (data && !Array.isArray(data)) {
    data = data.data
  }
  if (typeof props.filter === 'function' && isDefinedAndNotEmpty(props.filterData)) {
    data = props.filter(data, props.filterData)
  }

  return (
    <Select
      style={{ width: '100%' }}
      {...props}
      value={value}
      notFoundContent={errorOccurred ? localize('Common.InternalServerError') : null}
      onChange={onChange}>
      {groupOptions()}
    </Select>)
})

DropDownList.propTypes = {
  dataSource: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
  onChange: PropTypes.func,
  value: PropTypes.any,
  textField: PropTypes.string,
  valueField: PropTypes.string,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  clientSide: PropTypes.bool,
  isCodeList: PropTypes.bool,
  originalValue: PropTypes.any,
  itemsDetail: PropTypes.any,
  noSorting: PropTypes.bool,
  // Point of refreshValue is to give option to refresh the list source since changing dataSource does not work reliably
  refreshValue: PropTypes.any
}



// WEBPACK FOOTER //
// ./src/components/dropDownList.js